import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import ContactIcon from '@/shared/components/ContactIcon';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import Button from '@/shared/components/Button';
import Checkbox from '@/shared/components/input/Checkbox';
import EditableSelect from '@/shared/components/editable/EditableSelect';
import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { injectStateService, StateService } from '@/shared/services/StateService';
import DataCacheService from '@/shared/services/DataCacheService';
import dateFormatService from '@/shared/services/DateFormatService';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService from '@/shared/services/AuthService';
import api from '@/shared/api';
import messages from './employeeMessages';
import FormattedCurrency from '@/shared/components/FormattedCurrency';

const EmployeeOverviewRow = ({
  item: employee,
  columnStatus,
  intl,
  currentUser,
  stateService,
  employeeProfiles,
  onDeleteEmployee,
  onRestoreEmployee,
  onEmployeeUpdate,
  onSelectEmployee,
  usesSSO,
  filters,
  licenseInformation,
}) => {
  const getOwnCompanyName = companyId => {
    if (!companyId || companyId === '00000000-0000-0000-0000-000000000000') return '';
    const dataCache = DataCacheService.getDataCache();
    const companyObject = dataCache?.companyObject;
    if (!companyObject) return '';
    const company = companyObject[companyId];
    return company ? company.name : '';
  };

  const renderTeams = employee => {
    if (!employee || !employee.teams) return '';
    return employee.teams.map(x => x.name).join(', ');
  };

  const getLicenseInfo = lic => {
    if (!lic) return lic;
    if (lic.isFree) {
      return {
        ...lic,
        icon: 'fa-gift',
        info: intl.formatMessage(messages.FreeLicense),
      };
    }
    if (!lic.isTemporary) {
      return {
        ...lic,
        icon: 'fa-check',
        info: intl.formatMessage(messages.StandardLicense),
      };
    }
    const dateFormat = dateFormatService.dateFormat();
    const beginDate = moment(lic.beginDate);
    const endDate = moment(lic.endDate);

    const info = `${intl.formatMessage(messages.TemporaryLicense)}: ${beginDate.format(
      dateFormat,
    )} - ${endDate.format(dateFormat)}`;

    if (beginDate.isAfter(moment())) {
      return { ...lic, icon: 'fa-hourglass-start text-blue', info };
    }
    if (endDate.isAfter(moment())) {
      return { ...lic, icon: 'fa-hourglass-half', info };
    }
    return { ...lic, icon: 'fa-hourglass-end text-danger', info };
  };

  // Action handlers
  const handleOpen = () => {
    if (AuthService.hasPermission({ functionality: 'Users', privilege: 'Read' })) {
      stateService.gotoState('employees.detail', { id: employee.employeeID });
    }
  };

  const handleReset = async () => {
    if (!employee.userID || employee.saving) return;

    try {
      onEmployeeUpdate(employee.employeeID, { saving: true });
      await api.base.get(`/api/user/reset/${employee.userID}`);

      DataCacheService.updateUser(employee.userID, { userConfirmed: false });
      onEmployeeUpdate(employee.employeeID, { userConfirmed: false, saving: false });

      ToastrService.success(intl.formatMessage(messages.msgPasswordResetOtherUser));
    } catch (error) {
      handleHttpError(intl)(error);
      onEmployeeUpdate(employee.employeeID, { saving: false });
    }
  };

  const handleDelete = () => {
    if (!employee.isActive || employee.userID === currentUser?.userID || employee.saving) return;

    // Open delete confirmation modal
    onDeleteEmployee(employee);
  };

  const handleUndelete = () => {
    if (employee.isActive || employee.userID === currentUser?.userID || employee.saving) return;

    // Open restore employee modal
    onRestoreEmployee(employee);
  };

  const handleToggleHidden = async () => {
    if (employee.saving) return;

    try {
      onEmployeeUpdate(employee.employeeID, { saving: true });
      const hidden = !employee.hidden;
      await api.base.put(`/api/employee/Hidden/${employee.employeeID}/${hidden}`);

      DataCacheService.updateUser(employee.userID, { hidden });
      onEmployeeUpdate(employee.employeeID, { hidden, saving: false });

      ToastrService.success();
    } catch (error) {
      handleHttpError(intl)(error);
      onEmployeeUpdate(employee.employeeID, { saving: false });
    }
  };

  const handleEditEmployeeProfile = async profile => {
    console.log('Profile update called with:', profile);
    if (!profile || !profile.id) return false;
    if (employee.employeeProfile && employee.employeeProfile.id === profile.id) return false;

    try {
      await api.base.put('/api/employee/UpdateProfile', null, {
        params: {
          id: employee.employeeID,
          profileId: profile.id,
        },
      });

      onEmployeeUpdate(employee.employeeID, { employeeProfile: profile });
      return false;
    } catch (error) {
      handleHttpError(intl)(error);
      return '';
    }
  };

  const renderCell = col => {
    switch (col.key) {
      case 'phone':
        return (
          <>
            <ContactIcon type="phone" value={employee.employeePhone} />
          </>
        );
      case 'email':
        return (
          <>
            <ContactIcon type="email" value={employee.userEmail} />
          </>
        );
      case 'dateOfBirth':
        return <FormattedDateShort value={employee.employeeDateOfBirth} />;
      case 'profile':
        return (
          <EditableSelect
            value={employee.employeeProfile || null}
            onChange={handleEditEmployeeProfile}
            options={employeeProfiles}
            displayValue={
              employee.employeeProfile
                ? employeeProfiles.find(p => p.id === employee.employeeProfile.id)?.label
                : employee.employeeProfile?.name
            }
            submitOnChange={true}
            submitOnBlur={true}
            bindToId={false}
            required={true}
            disabled={!AuthService.hasPermission({ functionality: 'Users', privilege: 'Update' })}
          />
        );
      case 'company':
        return getOwnCompanyName(employee.companyId);
      case 'teams':
        return renderTeams(employee);
      case 'role':
        return (
          <>
            {(employee.userRoles || []).map(role => (
              <div key={role.role || role.customRoleId}>
                {role.customRoleName ||
                  intl.formatMessage(listItemMsg('role', role.role, role.role))}
              </div>
            ))}
          </>
        );
      case 'employeeType':
        return employee.employeeType
          ? intl.formatMessage(
              listItemMsg('employeeType', employee.employeeType, employee.employeeType),
            )
          : '';
      case 'employeeNumber':
        return employee.employeeNumber;
      case 'startDate':
        return <FormattedDateShort value={employee.employeeStartDate} />;
      case 'endDate':
        return <FormattedDateShort value={employee.endDate} />;
      case 'employmentStartDate':
        return <FormattedDateShort value={employee.employmentStartDate} />;
      case 'employmentEndDate':
        return <FormattedDateShort value={employee.employmentEndDate} />;
      case 'license':
        const processedLicense = employee.license?.id ? getLicenseInfo(employee.license) : null;
        return (
          <span className="text-center">
            {processedLicense ? (
              <i className={`fa ${processedLicense.icon}`} title={processedLicense.info}></i>
            ) : (
              <i className="fa fa-ban" title={intl.formatMessage(messages.NoLicense)}></i>
            )}
          </span>
        );
      case 'confirmed':
        return (
          <span className="text-center">
            {employee.userConfirmed && <span className="fa fa-check"></span>}
          </span>
        );
      case 'twoFaEnabled':
        return (
          <span className="text-center">
            {employee.twoFaEnabled && <span className="fa fa-check"></span>}
          </span>
        );
      case 'internalPrice':
        return employee.internalPrice !== null ? (
          <FormattedCurrency value={employee.internalPrice} />
        ) : null;
      case 'intraCompanyPrice':
        return employee.intraCompanyPrice !== null ? (
          <FormattedCurrency value={employee.intraCompanyPrice} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <tbody className="hover">
      <tr>
        <td>
          <Checkbox onClick={() => onSelectEmployee(employee)} value={!!employee.selected} />
        </td>
        <td
          onClick={handleOpen}
          className={
            AuthService.hasPermission({ functionality: 'Users', privilege: 'Read' })
              ? 'clickable'
              : !employee.isActive
              ? 'removed-user'
              : ''
          }
          title={
            AuthService.hasPermission({ functionality: 'Users', privilege: 'Read' })
              ? intl.formatMessage(messages.OpenDetails)
              : ''
          }
        >
          <span>
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Read' }) && (
              <i className="fa fa-external-link"></i>
            )}
            {employee.firstName} {employee.lastName}
          </span>
        </td>

        {/* Dynamic columns based on columnStatus */}
        {columnStatus.phone && <td>{renderCell({ key: 'phone' })}</td>}
        {columnStatus.email && <td>{renderCell({ key: 'email' })}</td>}
        {columnStatus.dateOfBirth && <td>{renderCell({ key: 'dateOfBirth' })}</td>}
        {columnStatus.profile && <td>{renderCell({ key: 'profile' })}</td>}
        {columnStatus.company && <td>{renderCell({ key: 'company' })}</td>}
        {columnStatus.teams && <td>{renderCell({ key: 'teams' })}</td>}
        {columnStatus.role && <td>{renderCell({ key: 'role' })}</td>}
        {columnStatus.employeeType && <td>{renderCell({ key: 'employeeType' })}</td>}
        {columnStatus.employeeNumber && <td>{renderCell({ key: 'employeeNumber' })}</td>}
        {columnStatus.internalPrice && (
          <td className="text-right">{renderCell({ key: 'internalPrice' })}</td>
        )}
        {columnStatus.intraCompanyPrice && (
          <td className="text-right">{renderCell({ key: 'intraCompanyPrice' })}</td>
        )}
        {columnStatus.startDate && <td>{renderCell({ key: 'startDate' })}</td>}
        {columnStatus.endDate && <td>{renderCell({ key: 'endDate' })}</td>}
        {columnStatus.employmentStartDate && <td>{renderCell({ key: 'employmentStartDate' })}</td>}
        {columnStatus.employmentEndDate && <td>{renderCell({ key: 'employmentEndDate' })}</td>}
        {columnStatus.license && <td className="text-center">{renderCell({ key: 'license' })}</td>}
        {columnStatus.confirmed && (
          <td className="text-center">{renderCell({ key: 'confirmed' })}</td>
        )}
        {columnStatus.twoFaEnabled && (
          <td className="text-center">{renderCell({ key: 'twoFaEnabled' })}</td>
        )}

        {AuthService.hasPermission({ functionality: 'Users', privilege: 'Update' }) && !usesSSO && (
          <td className="text-center">
            {employee.isActive && employee.userID !== currentUser?.userID && (
              <a
                onClick={handleReset}
                title={intl.formatMessage(messages.SendPasswordResetEmail)}
                className="fa fa-unlock-alt"
                style={{ cursor: employee.saving ? 'not-allowed' : 'pointer' }}
              />
            )}
          </td>
        )}

        {AuthService.hasPermission({ functionality: 'Users', privilege: 'Update' }) &&
          filters.activeEmployeeFilter !== 1 && (
            <td className="text-center">
              {!employee.isActive && (
                <a
                  onClick={handleToggleHidden}
                  title={intl.formatMessage(messages.VisibleInApplication)}
                  style={{ cursor: employee.saving ? 'not-allowed' : 'pointer' }}
                >
                  {employee.hidden ? (
                    <i
                      className="fa fa-eye-slash text-danger"
                      title={intl.formatMessage(messages.HiddenInApplication)}
                    />
                  ) : (
                    <i
                      className="fa fa-eye text-success"
                      title={intl.formatMessage(messages.VisibleInApplication)}
                    />
                  )}
                </a>
              )}
            </td>
          )}

        {AuthService.hasPermission({ functionality: 'Users', privilege: 'Delete' }) && (
          <td className="text-center">
            {employee.isActive && employee.userID !== currentUser?.userID ? (
              <a
                onClick={handleDelete}
                title={intl.formatMessage(messages.DeleteEmployee)}
                className="fa fa-trash-o"
                style={{ cursor: employee.saving ? 'not-allowed' : 'pointer' }}
              />
            ) : !employee.isActive &&
              employee.userID !== currentUser?.userID &&
              licenseInformation &&
              !licenseInformation.disableUserCreation ? (
              <a
                onClick={handleUndelete}
                title={intl.formatMessage(messages.RestoreEmployee)}
                className="fa fa-undo"
                style={{ cursor: employee.saving ? 'not-allowed' : 'pointer' }}
              />
            ) : null}
          </td>
        )}
      </tr>
    </tbody>
  );
};

EmployeeOverviewRow.propTypes = {
  item: PropTypes.object.isRequired,
  columnStatus: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  currentUser: PropTypes.object,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  employeeProfiles: PropTypes.array.isRequired,
  onDeleteEmployee: PropTypes.func.isRequired,
  onRestoreEmployee: PropTypes.func.isRequired,
  onEmployeeUpdate: PropTypes.func.isRequired,
  onSelectEmployee: PropTypes.func.isRequired,
  usesSSO: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  licenseInformation: PropTypes.object.isRequired,
};

export default injectStateService(injectIntl(EmployeeOverviewRow));
